import { TextInput } from '../../../components/FormUtils/TextInput'
import { useAuth } from 'context/authentication/useAuth'
import { useForm } from 'react-hook-form'
import { z } from 'zod'
import { zodResolver } from '@hookform/resolvers/zod'
import to from 'await-to-js'
import { handleFormError } from '../../../utils/handleFormError'
import { ErrorText } from '../../../components/FormUtils/ErrorText'
import FbButton from '../../../components/FbUI/FbButton'
import { FbLink } from 'components/FbUI/FbLink'
import { publicFeatureFlags } from '../../../utils/publicFeatureFlags'
import { useEffect, useState } from 'react'

const loginFormShape = z.object({
  email: z.string().email(),
  password: z.string(),
  non_field_errors: z.string().optional(),
})

function Login() {
  const { login, resendVerify } = useAuth()
  const [enableSSO, setEnableSSO] = useState(false)

  useEffect(() => {
    // Check if SSO is enabled using public feature flags
    const checkSSO = async () => {
      await publicFeatureFlags.loadFlags()
      const ssoEnabled = publicFeatureFlags.isEnabled('enable_sso')
      setEnableSSO(ssoEnabled)
    }
    checkSSO()
  }, [])

  const {
    control,
    handleSubmit,
    formState: { isSubmitting },
    setError,
    getValues,
  } = useForm({
    defaultValues: {
      email: '',
      password: '',
      non_field_errors: '',
    },
    resolver: zodResolver(loginFormShape),
  })

  const onSubmit = handleSubmit(
    async (values) => {
      const [err] = await to(login(values.email, values.password))
      handleFormError(err, setError)
    },
    (errors) => console.warn(errors)
  )

  const resendVerification = async () => {
    const [err] = await to(resendVerify(getValues()?.email))
    handleFormError(err, setError)
  }

  return (
    <form
      onSubmit={onSubmit}
      className="w-96 flex-shrink-0 max-[400px]:w-full max-[400px]:mx-4"
    >
      <h1 className="text-2xl mb-8">Log in to your account</h1>
      <TextInput control={control} name="email" label="Email" />
      <TextInput
        type="password"
        control={control}
        name="password"
        label="Password"
      />
      <div className="mt-4 flex items-center gap-2 flex-row-reverse">
        <FbButton type="submit" loading={isSubmitting}>
          Log In
        </FbButton>
        <FbLink to="/reset-password">Forgot Password?</FbLink>
      </div>
      <ErrorText
        resendVerification={resendVerification}
        control={control}
        name={'non_field_errors'}
      />

      {enableSSO && (
        <>
          <div className="flex items-center my-6">
            <div className="flex-1 h-px bg-gray-300"></div>
            <span className="px-3 text-gray-600 text-xs">or</span>
            <div className="flex-1 h-px bg-gray-300"></div>
          </div>

          <div className="flex justify-center mt-4">
            <FbLink to="/sso-login" className="w-full no-underline">
              <button
                type="button"
                className="w-full px-4 py-2.5 bg-gray-100 border border-gray-300 rounded text-sm text-gray-800 cursor-pointer transition-colors duration-200 hover:bg-gray-200"
              >
                Sign in with SSO
              </button>
            </FbLink>
          </div>
        </>
      )}
    </form>
  )
}

export default Login
